import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ChatBotStyleAndConfig } from '../../modals/style-and-config/style-config';
import { QnA } from '../../modals/qna/qna.interface';
import { ChatService } from '../../services/chat/chat.service';
import { SessionService } from '../../services/app-state-services/session/session.service';
import { ResponseStreamService } from '../../services/response-stream/response-stream.service';
import { ConfigService } from '../../../common/services/config/config.service';
import { ChatStateService } from '../../services/app-state-services/chat-main/chat-state.service';
import { IframeModeService } from '../../services/iframe-mode/iframe-mode.service';
import { IframeMode } from '../../../enums/iframe-mode.enum';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-chat-home',
  templateUrl: './chat-home.component.html',
  styleUrl: './chat-home.component.scss',
  standalone: false
})
export class ChatHomeComponent {
  @Output() closeModalEvent = new EventEmitter<boolean>();
  @ViewChildren("messageContainer") messageContainers!: QueryList<ElementRef>;
  @Input() isPhoneNumberSession: boolean = false;
  @Input() urlParams: any = {}; 
  @Input() iframeMode: IframeMode = IframeMode.NONE;
  @Input() hideCloseButton: boolean = false;

  userQna: QnA[] = [];
  currentUserQuery: string = ""
  currentIntent: string = "";
  queryResponseReceived: boolean = true;
  responseTypingSpeed = 50;
  isFullScreen: boolean = false;
  isLandingScreenShown = false;
  isBackendServerAvailable: boolean = true;
  isWelcomeScreen: boolean = true;
  isIframeMode: boolean = false;
  IframeMode = IframeMode; // Expose enum to template

  isInlineMode(): boolean {
    return this.iframeMode === IframeMode.INLINE;
  }

  constructor(
    private _chatStateService: ChatStateService,
    private _chatService: ChatService,
    private _sessionService: SessionService,
    private _responseStreamService: ResponseStreamService,
    private _configService: ConfigService,
    private _iframeModeService: IframeModeService,
    private cdr: ChangeDetectorRef,
  ) { }

  chatBotStyles!: ChatBotStyleAndConfig;

  ngOnInit(): void {
    this._configService.loadConfig();
    this.isIframeMode = this.iframeMode !== IframeMode.NONE || this._iframeModeService.isIframeMode();
    
    this.handleSubscriptions(); 
    this.handleVariableUpdates();
    this.handlePhoneCallScenario();
    
    setTimeout(() => {
      if (this.isIframeMode) {
        this._iframeModeService.sendMessageToParent('CHAT_READY', {
          status: 'ready',
          mode: this.iframeMode
        });
      }
    }, 100);
  }

  handleSubscriptions() {
    this._chatStateService.usersQna$.subscribe((qna: any) => this.userQna = qna);
    this._chatStateService.isQueryResponseReceived$.subscribe((value: any) => this.queryResponseReceived = value); 
    this._chatStateService.isChatWindowFullScreen$.subscribe((value: any) => this.isFullScreen = value); 
    this._configService.chatBotStyleAndConfig$.subscribe((config) => {this.chatBotStyles = config;});
    this._chatStateService.isWelcomeScreen$.subscribe((value: any) => {this.isWelcomeScreen = value}); 
  }

  handleVariableUpdates() {
    this.isLandingScreenShown = true;
  }

  openChatWindow(intent: string) {
    this.currentIntent = intent;
    this.isLandingScreenShown = false;

    const payload = {
      "intent": this.currentIntent,
      "ownerId": "DEALER"
    }

    var query = "";
    if (this.currentIntent == "BOOK_SERVICE") {
      query = "I want to book a service";
    }
    else if (this.currentIntent == "CANCEL_SERVICE") {
      query = "I want to cancel a service"
    }

    if (this.userQna.length == 0) {
      this._chatService.startChatWithIntent(payload).subscribe({
        next: (response: any) => {
          this._sessionService.setContextInfo(JSON.stringify(response))
          this.initConversation(query, response)
        },
        error: (error: any) => {
          this._chatStateService.setQueryResponseReceived(true); 
          this.isBackendServerAvailable = false; 
          console.error("Error starting chat with intent:", error);
        }
      })
    }
  }

  initConversation(query: string, request: any) {
    this._chatStateService.setQueryResponseReceived(false); 
    this._chatStateService.addInitialEmptyQuestionToUsersQna();
    const currentQnaId = this.userQna[0].id;
    this.currentUserQuery = query; 
    const tenantCode = this.chatBotStyles.tenantCode ?? "";
    this.getResponseStreamAndUpdateQnA(currentQnaId, false, tenantCode);
  }

  getResponseStreamAndUpdateQnA(currentQnAId: string, isInputInVoice: boolean, tenantCode: string) {
    this._responseStreamService.connect(`${environment.apiURL}chat`, this.currentUserQuery, tenantCode).subscribe(
      (response: any) => {
        if (response != "[END_OF_STREAM]" && response != "{'': ''}") {
          this.cdr.detectChanges();
          this._chatStateService.updateQueryResponseStream(currentQnAId, response.replaceAll('"','')); // append response stream to answer
          this.applyTypingEffect(this.userQna.length - 1, response);
        }
        else if (response == "[END_OF_STREAM]") {
          this._chatStateService.updateQueryResponseId(currentQnAId, "ANSWER_id")
          this._chatStateService.setQueryResponseReceived(true); 
          this.cdr.detectChanges();
        }
      },
      (error) => {
        this._chatStateService.setQueryResponseReceived(true); 
        console.error("Error:", error); 
      }
    );
  }

  applyTypingEffect(index: number, message: string) {
    const containers = this.messageContainers.toArray();
    if (index >= 0 && index < containers.length) {
      const container = containers[index];
      const element = container.nativeElement;
      this._chatStateService.typeWriter(
        message,
        element,
        this.responseTypingSpeed
      );
    }
  }

  resizeChatWindow() {
    this._chatStateService.setChatWindowFullScreen(!this.isFullScreen);
  }

  closeChatModal() {
    this.closeModalEvent.emit(false);
  }

  refreshIframe() {
    if (this.isIframeMode || this.iframeMode !== IframeMode.NONE) {
      window.location.reload();
    } else {
      this.closeChatModal();
    }
  }

  async handlePhoneCallScenario() {
    if (this.isPhoneNumberSession) {
      this.isWelcomeScreen = false;
      this._chatStateService.setIsWelcomeScreen(false);

      const existingContext = this._sessionService.getContextInfo();
      if (existingContext) {
        return;
      }

      this._chatStateService.clearUsersQna();
      
      const customerName = '';
      this._chatStateService.setCustomerName(customerName);
      
      const payload = {
        intent: this.urlParams.service,
        ownerId: this.chatBotStyles?.tenantCode,
        customerName: customerName,
        phoneNumber: this.urlParams.phone_number
      };

      try {
        const response = await firstValueFrom(this._chatService.startChatWithIntent(payload));
        
        this._sessionService.setContextInfo(JSON.stringify(response));
        this._chatStateService.setCustomerWelcomeMessage(response.customerWelcomeMessage);
        
        const currentQnA = this._chatStateService.getQna();
        currentQnA.answer.message = response.customerWelcomeMessage;
        currentQnA.answer.receivedFrom = "VirtualAgent";
        this._chatStateService.addEntryToUsersQna(currentQnA);
        this._chatStateService.updateQueryResponseId(currentQnA.id, "ANSWER_id");
        
      } catch (error) {
        console.error("Error starting phone call chat:", error);
        this.isBackendServerAvailable = false;
      }
    }
  }
}

